import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { X, Shield, Clock, AlertCircle } from 'lucide-react';

interface RequestAdminDialogProps {
  clientId: string;
  clientName: string;
  partnerTier: string;
  isOpen: boolean;
  onClose: () => void;
}

export function RequestAdminDialog({
  clientId,
  clientName,
  partnerTier,
  isOpen,
  onClose,
}: RequestAdminDialogProps) {
  const [reason, setReason] = useState('');
  const [durationHours, setDurationHours] = useState<number>(4);
  const [customDuration, setCustomDuration] = useState('');
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const reasonTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Tier-based duration limits
  const maxDuration = {
    free_partner: 24,
    agency_partner: 720, // 30 days
    enterprise_partner: null, // No limit
  }[partnerTier] || 24;

  const mutation = useMutation({
    mutationFn: () => api.requestAdminAccess(clientId, reason, durationHours),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-grant', clientId] });
      setReason('');
      setDurationHours(4);
      setCustomDuration('');
      onClose();
    },
  });

  // Focus management and keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    // Focus the textarea when dialog opens
    reasonTextareaRef.current?.focus();

    // Trap focus within dialog
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }

      if (e.key === 'Tab' && dialogRef.current) {
        const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!reason.trim()) {
      alert('Please provide a reason for requesting admin access');
      return;
    }

    if (maxDuration && durationHours > maxDuration) {
      alert(`Your tier allows max ${maxDuration} hours`);
      return;
    }

    mutation.mutate();
  };

  const handleDurationChange = (value: string) => {
    if (value === 'custom') {
      setCustomDuration('');
    } else {
      const hours = parseInt(value);
      setDurationHours(hours);
      setCustomDuration('');
    }
  };

  const handleCustomDurationChange = (value: string) => {
    setCustomDuration(value);
    const hours = parseInt(value);
    if (!isNaN(hours) && hours > 0) {
      setDurationHours(hours);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="request-admin-dialog-title"
      aria-describedby="request-admin-dialog-description"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className="relative w-full max-w-lg bg-slate-800 rounded-lg shadow-xl border border-slate-700 mx-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-red-400" aria-hidden="true" />
            <h2
              id="request-admin-dialog-title"
              className="text-xl font-semibold text-slate-100"
            >
              Request Admin Access
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-700 transition-colors"
            aria-label="Close dialog"
          >
            <X className="h-5 w-5 text-slate-400" aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6" aria-label="Request admin access form">
          {/* Client Info */}
          <div
            id="request-admin-dialog-description"
            className="bg-slate-900/50 rounded-lg p-4 border border-slate-700"
          >
            <p className="text-sm text-slate-400">Requesting admin access for:</p>
            <p className="mt-1 text-base font-medium text-slate-100">{clientName}</p>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <label htmlFor="admin-request-reason" className="block text-sm font-medium text-slate-300">
              Reason <span className="text-red-400" aria-label="required">*</span>
            </label>
            <textarea
              id="admin-request-reason"
              ref={reasonTextareaRef}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why you need temporary admin access..."
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-300 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              rows={4}
              required
              aria-required="true"
              aria-describedby="reason-help-text"
            />
            <p id="reason-help-text" className="text-xs text-slate-500">
              This will be sent to the client for approval
            </p>
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <label id="duration-label" className="block text-sm font-medium text-slate-300">
              Duration <span className="text-red-400" aria-label="required">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-labelledby="duration-label">
              {[
                { label: '1 hour', value: 1 },
                { label: '4 hours', value: 4 },
                { label: '24 hours', value: 24 },
                { label: '7 days', value: 168 },
                ...(partnerTier !== 'free_partner' ? [{ label: '30 days', value: 720 }] : []),
              ]
                .filter(opt => !maxDuration || opt.value <= maxDuration)
                .map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleDurationChange(option.value.toString())}
                    role="radio"
                    aria-checked={durationHours === option.value && !customDuration}
                    aria-label={`${option.label} duration`}
                    className={`px-4 py-2 rounded-lg border transition-colors ${
                      durationHours === option.value && !customDuration
                        ? 'bg-red-900/30 border-red-700 text-red-300'
                        : 'bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-600'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              <button
                type="button"
                onClick={() => handleDurationChange('custom')}
                role="radio"
                aria-checked={!!customDuration}
                aria-label="Custom duration"
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  customDuration
                    ? 'bg-red-900/30 border-red-700 text-red-300'
                    : 'bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-600'
                }`}
              >
                Custom
              </button>
            </div>

            {customDuration !== undefined && customDuration !== null && (
              <div className="mt-2">
                <label htmlFor="custom-duration-input" className="sr-only">
                  Custom duration in hours
                </label>
                <input
                  id="custom-duration-input"
                  type="number"
                  value={customDuration}
                  onChange={(e) => handleCustomDurationChange(e.target.value)}
                  placeholder="Hours"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-300 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  min="1"
                  max={maxDuration || undefined}
                  aria-label="Custom duration in hours"
                  aria-describedby="duration-help-text"
                />
              </div>
            )}

            <div id="duration-help-text" className="flex items-start gap-2 text-xs text-slate-500">
              <Clock className="h-3 w-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span>
                Your tier ({partnerTier.replace('_', ' ')}) allows max{' '}
                {maxDuration ? `${maxDuration} hours` : 'unlimited duration'}
              </span>
            </div>
          </div>

          {/* Warning */}
          <div
            className="flex items-start gap-3 p-4 bg-amber-900/20 border border-amber-800 rounded-lg"
            role="alert"
            aria-label="Important information about admin access request"
          >
            <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="text-sm text-amber-300">
              <p className="font-medium">Client approval required</p>
              <p className="mt-1 text-amber-400">
                The client will receive an email to approve or reject this request.
                All actions taken during admin access will be logged.
              </p>
            </div>
          </div>

          {/* Error Message */}
          {mutation.isError && (
            <div
              className="flex items-start gap-3 p-4 bg-red-900/20 border border-red-800 rounded-lg"
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
            >
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div className="text-sm text-red-300">
                <p className="font-medium">Request failed</p>
                <p className="mt-1 text-red-400">
                  {(mutation.error as Error)?.message || 'An error occurred'}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-700/50 transition-colors"
              disabled={mutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={mutation.isPending || !reason.trim()}
            >
              {mutation.isPending ? 'Requesting...' : 'Request Admin Access'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
